import { useTranslation } from 'react-i18next';
import { SectionEntry } from 'entities/document';
import { useDocumentParams } from 'app/router/params';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { OldModal } from 'app/components';
import { OldModalHandle } from 'shared/overlays/OldModal';
import { useUser } from 'app/state/contexts/UserContext';
import Tippy from '@tippyjs/react';
import { followCursor } from 'tippy.js';
import { LazyTippy } from 'app/components';
import { ToolMenu } from 'app/components';
import Select from 'react-select';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSectionPermissions,
  useCreatePermissionGroup,
  useUpdatePermissionGroup,
  useDeletePermissionGroup,
  useAddUserToPermissionGroup,
  useRemoveUserFromPermissionGroup,
  useChangeSectionPermission,
  permissionGroupKeys,
} from 'entities/permission-group';
import { documentPermissionKeys } from 'entities/document-permission';

interface GroupMember {
  userId: number;
  permissionGroupId: number;
  [key: string]: unknown;
}

interface PermissionGroup {
  id: number;
  name?: string;
  UserPermissionGroups?: GroupMember[];
  [key: string]: unknown;
}

interface PermissionUser {
  id: number;
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

interface UserSectionPermission {
  sectionSuperId: string;
  userId: number;
  permission?: string;
}

interface PermissionMenuItem {
  title?: string;
  icon?: string;
  subtitle?: string;
  permissionName: string;
}

interface SectionPermissionsPermissions {
  permissionGroups: PermissionGroup[];
  userSectionPermissions?: UserSectionPermission[];
  userDocumentPermissions?: { userId: number; permission: string }[];
}

interface SelectedDocument {
  content?: { sections?: SectionEntry[] };
}

interface SectionPermissionsData {
  permissions?: SectionPermissionsPermissions;
  users?: PermissionUser[];
  document?: SelectedDocument;
}

interface NewGroupFormProps {
  documentId: string;
  onGroupCreated: () => void;
  existingPermissionGroup: PermissionGroup | null;
}

const useRefreshSectionPermissions = () => {
  const queryClient = useQueryClient();
  const { documentId } = useDocumentParams();
  return () => {
    queryClient.invalidateQueries({ queryKey: [...permissionGroupKeys.sectionPermissionsAll] });
    queryClient.invalidateQueries({ queryKey: documentPermissionKeys.forDocument(documentId) });
  };
};

const NewGroupForm = ({ documentId, onGroupCreated, existingPermissionGroup }: NewGroupFormProps) => {
  const [newPermissionGroupName, setNewPermissionGroupName] = useState(existingPermissionGroup ? existingPermissionGroup.name : '');
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: createGroup } = useCreatePermissionGroup();
  const { mutate: updateGroup } = useUpdatePermissionGroup();
  const refreshPermissions = useRefreshSectionPermissions();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const createPermissionGroup = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newPermissionGroupName) {
      return;
    }

    const onSuccess = () => {
      setNewPermissionGroupName('');
      refreshPermissions();
      onGroupCreated();
    };

    if (existingPermissionGroup) {
      updateGroup({ id: existingPermissionGroup.id, name: newPermissionGroupName, documentSuperId: documentId }, { onSuccess });
    } else {
      createGroup({ name: newPermissionGroupName, documentSuperId: documentId }, { onSuccess });
    }
  };

  return (
    <div className="add-new-popup wider-popup popup-with-button">
      <div className="comment--response">
        <form onSubmit={createPermissionGroup}>
          <input
            className="input-variant wider-input"
            type="text"
            name="username"
            value={newPermissionGroupName}
            ref={inputRef}
            placeholder=""
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setNewPermissionGroupName(event.target.value);
            }}
          />
          <button type="submit" className="button btn btn-secondary">
            {existingPermissionGroup ? 'Uppdatera gruppnamn' : 'Skapa ny grupp'}
          </button>
        </form>
      </div>
    </div>
  );
};

interface NewUserFormProps {
  permissionGroup: PermissionGroup;
  onUserAdded: () => void;
}

const NewUserForm = ({ permissionGroup, onUserAdded }: NewUserFormProps) => {
  const { t } = useTranslation('section-permissions');

  const [userName, setUserName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: addUser } = useAddUserToPermissionGroup();
  const { selectedOrganization } = useUser();
  const refreshPermissions = useRefreshSectionPermissions();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleAddUser = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userName) {
      return;
    }

    addUser(
      { permissionGroupId: permissionGroup.id, userName, organization: selectedOrganization },
      {
        onSuccess: () => {
          refreshPermissions();
          setUserName('');
          onUserAdded();
        },
      }
    );
  };

  return (
    <div className="add-new-popup wider-popup popup-with-button">
      <div className="comment--response">
        <form onSubmit={handleAddUser}>
          <input
            className="input-variant wider-input"
            type="text"
            name="username"
            value={userName}
            ref={inputRef}
            placeholder={t('group.field.add-new-user')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setUserName(event.target.value);
            }}
          />
          <button type="submit" className="button btn btn-secondary">
            {t('group.button.add-new-user')} {userName}
          </button>
        </form>
      </div>
    </div>
  );
};

interface SectionMenuItemProps {
  menuItem: PermissionMenuItem;
  onClick: (permissionName: string) => void;
}

interface SelectPermissionProps {
  menuItems: PermissionMenuItem[];
  onClick: (sectionSuperId: string, userId: number, menuItemId: string) => void;
  sectionSuperId: string;
  userId: number;
}

interface SelectedSectionPermissionProps {
  superId: string;
  permission: PermissionMenuItem | null;
  userId: number;
  index: number;
}

const SectionPermissions = () => {
  const { t } = useTranslation(['common', 'document-permissions', 'section-permissions']);

  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const { data } = useSectionPermissions(documentId, selectedOrganization) as { data?: SectionPermissionsData };
  const permissions: SectionPermissionsPermissions = data?.permissions ?? { permissionGroups: [] };
  const users = data?.users ?? [];
  const selectedDocument: SelectedDocument = data?.document ?? {};
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const permissionSectionMenuItems = (item: PermissionGroup) => [
    { text: t('section-permissions:group.dropdown-item.change-name'), icon: 'fa-regular fa-pen', onClick: () => ChangeName(item) },
    { text: t('section-permissions:group.dropdown-item.change-time-limit'), icon: 'fa-regular fa-clock', disabled: true },
    { text: t('section-permissions:group.dropdown-item.delete-group'), icon: 'fa-regular fa-trash-can', onClick: () => deletePermissionGroup(item) },
    { text: t('section-permissions:group.dropdown-item.move-down'), icon: 'fa-regular fa-arrow-down', disabled: true },
  ];
  const menuItemsEditPermissionGroupUser = (groupMember: GroupMember) => [
    { text: t('section-permissions:user.dropdown-item.change-all'), icon: 'fa-regular fa-key', disabled: true },
    { text: t('section-permissions:group.dropdown-item.remove-user'), icon: 'fa-regular fa-trash-can', onClick: () => removeUserFromGroup(groupMember) },
    { text: t('section-permissions:group.dropdown-item.move-to'), icon: 'fa-regular fa-folder', disabled: true },
  ];
  const [existingPermissionGroup, setExistingPermissionGroup] = useState<PermissionGroup | null>(null);

  //TODO Maybe fetch this list from backend, gaby-documents or client-config
  const AVAILABLE_SECTION_PERMISSIONS = [
    {
      title: t('section-permissions:dropdown.permission.write.title'),
      icon: 'fa-regular fa-pen',
      subtitle: t('section-permissions:dropdown.permission.write.text'),
      permissionName: 'WRITE',
    },
    {
      title: t('section-permissions:dropdown.permission.comment.title'),
      icon: 'fa-regular fa-message',
      subtitle: t('section-permissions:dropdown.permission.comment.text'),
      permissionName: 'COMMENT',
    },
    {
      title: t('section-permissions:dropdown.permission.remove.title'),
      icon: 'fa-regular fa-eye-slash',
      subtitle: t('section-permissions:dropdown.permission.remove.text'),
      permissionName: 'NO_PERMISSION',
    },
  ];

  const canUserTag = (userId: number) =>
    permissions.userDocumentPermissions?.some((p) => p.userId === userId && (p.permission === 'TAG_EDITOR' || p.permission === 'TAG_ADMIN'));

  const sectionPermissionsForUser = (userId: number) =>
    canUserTag(userId)
      ? AVAILABLE_SECTION_PERMISSIONS.map((p) =>
          p.permissionName === 'COMMENT'
            ? {
                ...p,
                title: t('section-permissions:dropdown.permission.comment-and-tag.title'),
                subtitle: t('section-permissions:dropdown.permission.comment-and-tag.text'),
              }
            : p
        )
      : AVAILABLE_SECTION_PERMISSIONS;

  const { mutate: deleteGroup } = useDeletePermissionGroup();
  const { mutate: removeFromGroup } = useRemoveUserFromPermissionGroup();
  const { mutate: changeSectionPerm } = useChangeSectionPermission();
  const deleteModal = useRef<OldModalHandle>(null);

  const invalidateAll = useRefreshSectionPermissions();

  const ChangeName = (group: PermissionGroup) => {
    setExistingPermissionGroup(group);
    setShowNewGroupForm(true);
  };

  const handleGroupDelete = (group: PermissionGroup | null, event: React.SyntheticEvent) => {
    event.preventDefault();
    if (!group) return;

    deleteGroup(
      { id: group.id },
      {
        onSuccess: () => {
          deleteModal.current?.close();
          invalidateAll();
        },
        onError: () => {
          deleteModal.current?.close();
        },
      }
    );
  };

  const deletePermissionGroup = (group: PermissionGroup) => {
    setExistingPermissionGroup(group);
    deleteModal.current?.open();
  };

  const removeUserFromGroup = (user: GroupMember) => {
    removeFromGroup({ permissionGroupId: user.permissionGroupId, userId: user.userId }, { onSuccess: invalidateAll });
  };

  const changeSectionPermission = (sectionSuperId: string, userId: number, selectedPermission: string) => {
    const currentPermission = findUserPermissionForSection(sectionSuperId, userId);
    changeSectionPerm({ sectionSuperId, userId, permission: selectedPermission, currentPermission }, { onSuccess: invalidateAll });
  };

  const findUserPermissionForSection = (sectionSuperId: string, userId: number) => {
    return permissions.userSectionPermissions?.find((permission) => permission.sectionSuperId === sectionSuperId && permission.userId === userId)?.permission;
  };

  const SectionMenuItem = ({ menuItem, onClick }: SectionMenuItemProps) => {
    const handleOnClick = () => {
      onClick(menuItem.permissionName);
    };
    return (
      <div className="tool-menu-item" onClick={handleOnClick}>
        <i className={`fa-regular ${menuItem.icon}`} />
        <span className="tool-menu-title">{menuItem.title}</span>
        <p className="tool-menu-subtitle">{menuItem.subtitle}</p>
      </div>
    );
  };

  const SelectPermission = ({ menuItems, onClick, sectionSuperId, userId }: SelectPermissionProps) => {
    const handleOnClick = (menuItemId: string) => {
      onClick(sectionSuperId, userId, menuItemId);
    };
    return (
      <div className="tool-menu-wrapper section-permission-tool-menu">
        {menuItems?.map((m, i) => {
          return <SectionMenuItem menuItem={m} key={i} onClick={handleOnClick} />;
        })}
      </div>
    );
  };

  const SelectedSectionPermission = ({ superId, permission, userId, index }: SelectedSectionPermissionProps) => {
    return (
      <Tippy
        interactive
        placement="bottom"
        followCursor="initial"
        trigger="click"
        plugins={[followCursor]}
        content={<SelectPermission menuItems={sectionPermissionsForUser(userId)} sectionSuperId={superId} userId={userId} onClick={changeSectionPermission} />}
        key={index}
      >
        <i className={`${permission?.icon ? permission.icon : 'fa-regular fa-eye-slash'} selected-permission-icon`} />
      </Tippy>
    );
  };

  const handleNewGroupClick = () => {
    setShowNewGroupForm(true);
    setExistingPermissionGroup(null);
  };

  const handleGroupCreated = () => {
    setShowNewGroupForm(false);
  };

  const onUserAdded = () => {};
  return (
    <div>
      {permissions.permissionGroups.map((group, i) => (
        <div key={group.id} className="section-permission-wrapper">
          <div className={i === 0 ? 'permission-section-header head-w-new-group-btn' : 'permission-section-header'}>
            <h2 className="permission-title">{group.name}</h2>
            {i !== 0 ? (
              <Tippy interactive={true} placement="bottom" trigger="click" content={<ToolMenu menuItems={permissionSectionMenuItems(group)} />}>
                <i className="fa-regular fa-ellipsis-v more toggle-icon edit-permission-group" />
              </Tippy>
            ) : null}
            {i === 0 ? (
              <LazyTippy
                interactive={true}
                visible={showNewGroupForm}
                onClickOutside={() => setShowNewGroupForm(false)}
                placement="bottom"
                content={<NewGroupForm documentId={documentId} onGroupCreated={handleGroupCreated} existingPermissionGroup={existingPermissionGroup} />}
              >
                <button className="button light-blue" type="button" onClick={() => handleNewGroupClick()}>
                  {t('section-permissions:button.new-group')}
                </button>
              </LazyTippy>
            ) : (
              <div className="permission-group-history columns">
                <p>{t('section-permissions:dropdown.history.title')}</p>
                <form>
                  <Select name="Historik" placeholder="Version 1" options={[]} isDisabled value={null} />
                </form>
              </div>
            )}
            <LazyTippy
              interactive={true}
              trigger="click"
              offset={[20, 10]}
              placement="bottom"
              content={<NewUserForm permissionGroup={group} onUserAdded={onUserAdded} />}
            >
              <button className="fab" type="button">
                <i className="fa-regular fa-plus font-15" />
              </button>
            </LazyTippy>
          </div>

          {/* TODO - Make kebab menu of this instead
          onClick={() => {
              removeUserPermission(section.superId, user.id, findUserPermissionForSection(section.superId, user.id));
            }}*/}

          <div className="responsive-table">
            <table className="permissions-table">
              <thead>
                <tr>
                  <td className="permission-thead-menu td-space"></td>
                  {selectedDocument.content?.sections?.map((section) => (
                    <td key={section.superId} className="section-thead">
                      <span>{section.name} </span>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(group.UserPermissionGroups ?? []).map((member) => {
                  const user = users.find((user) => user.id === member.userId);
                  if (!user) {
                    return (
                      <tr key={member.permissionGroupId + '-' + member.userId}>
                        <td>{t('common:loading-message')}</td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={user.id}>
                      <td className="td-space">
                        <div className="d-flex">
                          <div className="mr-3">
                            {user.firstName} {user.lastName}
                          </div>
                          <Tippy
                            interactive={true}
                            offset={[90, -140]}
                            trigger="click"
                            content={<ToolMenu menuItems={menuItemsEditPermissionGroupUser(member)} />}
                          >
                            <i className="fa-regular fa-ellipsis-v more toggle-icon edit-permission-group edit-permission-group-user" />
                          </Tippy>
                        </div>
                      </td>
                      {selectedDocument.content?.sections?.map((section) => (
                        <td key={section.superId}>
                          {AVAILABLE_SECTION_PERMISSIONS.map((permission, i) => (
                            <div key={group.id + section.superId + user.id + permission.permissionName}>
                              {findUserPermissionForSection(section.superId, user.id) === permission.permissionName ? (
                                <span>
                                  <SelectedSectionPermission superId={section.superId} permission={permission} userId={user.id} index={i} />
                                </span>
                              ) : null}
                            </div>
                          ))}
                          {!findUserPermissionForSection(section.superId, user.id) ? (
                            <SelectedSectionPermission superId={section.superId} permission={null} userId={user.id} index={0} />
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <OldModal ref={deleteModal} modalWrapperClass="delete-modal">
        <i
          className="fa-regular fa-xmark text-right close"
          onClick={() => {
            deleteModal.current?.close();
          }}
        />
        <form className="form" onSubmit={(e) => handleGroupDelete(existingPermissionGroup, e)}>
          <div>
            <i className="fa-regular fa-trash-can modal-icon" />
            <h3 className="modal-body-title">{t('section-permissions:modal.delete.title')}</h3>
            <p className="modal-body-text">
              {t('section-permissions:modal.delete.text')} {existingPermissionGroup?.name}?
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="button clear" onClick={() => deleteModal.current?.close()}>
              {t('common:button.cancel')}
            </button>
            <button type="submit" className="button primary" onClick={(e) => handleGroupDelete(existingPermissionGroup, e)}>
              {t('document-permissions:remove-user.modal.button.remove')}
            </button>
          </div>
        </form>
      </OldModal>
    </div>
  );
};

export default SectionPermissions;
